import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import HttpBackend from 'i18next-http-backend';

console.log('DIAGNOSTIC: i18n.ts - Starting i18n initialization');

/**
 * Keep <html lang> in sync with the active UI language.
 *
 * index.html hardcodes lang="en", so a Spanish UI used to be served declared as
 * English. Browsers trust the declared language, so they offer (or, with
 * "always translate English" on, silently perform) an English→Spanish
 * translation of a page that is already Spanish. Page translators rewrite text
 * nodes — Google Translate swaps them for <font> wrappers — which detaches the
 * very text nodes React holds in its fiber tree. The next commit that has to
 * insert an element *before* one of them then dies in insertBefore with
 * "NotFoundError: The object can not be found here", taking out the whole app
 * via the global error boundary. Declaring the real language stops the browser
 * from translating in the first place.
 */
const syncHtmlLang = (lng?: string) => {
  if (typeof document === 'undefined') return;
  const lang = (lng || i18n.resolvedLanguage || i18n.language || 'en').split('-')[0];
  if (document.documentElement.lang !== lang) {
    document.documentElement.lang = lang;
  }
};

i18n.on('languageChanged', syncHtmlLang);

i18n
  // Load translations using http backend
  .use(HttpBackend)
  // Detect user language
  .use(LanguageDetector)
  // Pass the i18n instance to react-i18next
  .use(initReactI18next)
  // Initialize i18next
  .init({
    // An unsupported browser language (fr, pt…) lands on Spanish, the
    // language of the farms; a key missing in Spanish still falls back to
    // English text rather than its raw name.
    fallbackLng: ['es', 'en'],
    debug: import.meta.env.DEV, // Enable debug in development
    
    // Supported languages
    supportedLngs: ['en', 'es'],
    
    // Language detection settings
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
      lookupLocalStorage: 'i18nextLng',
    },
    
    // Backend configuration
    backend: {
      loadPath: '/locales/{{lng}}/{{ns}}.json',
    },
    
    // Add error handling
    saveMissing: false,
    missingKeyHandler: (lng, ns, key) => {
      console.log('DIAGNOSTIC: i18n.ts - Missing key:', { lng, ns, key });
    },
    
    // Namespaces
    ns: ['common', 'alerts', 'weather', 'schedules', 'forms', 'email', 'units', 'auth', 'pages', 'devices', 'admin', 'farmAccess', 'farms', 'onboarding', 'dashboard', 'profile', 'waterBalance', 'irrigationSeason', 'irrigationSectors', 'irrigationZones', 'zones', 'fields', 'cwsi', 'programs', 'irrigationLog', 'tasks', 'pumps', 'opta', 'mapLayers'],
    defaultNS: 'common',
    
    // Interpolation settings
    interpolation: {
      escapeValue: false, // React already escapes values
    },
    
    // React-i18next options
    react: {
      useSuspense: false,
    },
  })
  .then(() => {
    // languageChanged has usually fired by now, but detection can resolve to the
    // fallback without emitting it — set it explicitly so lang is never stale.
    syncHtmlLang();
    console.log('DIAGNOSTIC: i18n.ts - i18n initialized successfully');
    console.log('DIAGNOSTIC: i18n.ts - Current language:', i18n.language);
    console.log('DIAGNOSTIC: i18n.ts - Store data:', i18n.store.data);
    console.log('DIAGNOSTIC: i18n.ts - Store data keys:', Object.keys(i18n.store.data));
    console.log('DIAGNOSTIC: i18n.ts - Has admin namespace (es)?', i18n.hasResourceBundle('es', 'admin'));
    console.log('DIAGNOSTIC: i18n.ts - Has devices namespace (es)?', i18n.hasResourceBundle('es', 'devices'));
    console.log('DIAGNOSTIC: i18n.ts - admin namespace content (es):', i18n.getResourceBundle('es', 'admin'));
    console.log('DIAGNOSTIC: i18n.ts - devices namespace content (es):', i18n.getResourceBundle('es', 'devices'));
    console.log('DIAGNOSTIC: i18n.ts - admin.users.title:', i18n.t('admin:users.title'));
    console.log('DIAGNOSTIC: i18n.ts - devices.association.manageForUser:', i18n.t('devices:association.manageForUser'));
    
    // Listen for resource loading
    i18n.on('loaded', (loaded) => {
      console.log('DIAGNOSTIC: i18n.ts - Resources loaded:', loaded);
    });
    
    i18n.on('failedLoading', (lng, ns, msg) => {
      console.error('DIAGNOSTIC: i18n.ts - Failed loading:', { lng, ns, msg });
    });
  })
  .catch((error) => {
    console.error('DIAGNOSTIC: i18n.ts - i18n initialization failed:', error);
  });

export default i18n;
